import React, { useEffect, useState } from 'react';
import { 
  Box, Typography, Table, TableHead, TableRow, TableCell, 
  TableBody, TableContainer, Paper, Button, IconButton, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Chip, Switch, FormControlLabel, MenuItem
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import equipmentService from '../../services/equipmentService';
import SkeletonBlock from '../ui/SkeletonBlock';
import { useToast } from '../ToastProvider';

export default function EquipmentSubcategoriesAdmin() {
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', estimatedPrice: '', isActive: true });
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  useEffect(() => { loadCategories(); }, []);

  useEffect(() => {
    loadSubcategories();
  }, [selectedCategoryId]);

  const loadCategories = async () => {
    try {
      const data = await equipmentService.getCategories(true);
      setCategories(Array.isArray(data) ? data : []);
    } catch (e) {
      setError('Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  const loadSubcategories = async () => {
    setLoading(true);
    try {
      // Pass null/empty to get all, or categoryId to filter
      const categoryIdParam = selectedCategoryId || null;
      const data = await equipmentService.getSubcategories(categoryIdParam);
      setSubcategories(Array.isArray(data) ? data : []);
    } catch (e) {
      setError('Failed to load item types');
    } finally {
      setLoading(false);
    }
  };

  const openDialog = (item = null) => {
    setEditingItem(item);
    setForm(item ? {
      categoryId: item.categoryId || selectedCategoryId,
      name: item.name,
      description: item.description || '',
      estimatedPrice: item.estimatedPrice || '',
      isActive: item.isActive
    } : { categoryId: selectedCategoryId, name: '', description: '', estimatedPrice: '', isActive: true });
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingItem(null);
  };

  const handleSave = async () => {
    if (!form.categoryId) { toast.error('Category is required'); return; }
    if (!form.name.trim()) { toast.error('Name is required'); return; }

    setSaving(true);
    try {
      const payload = {
        categoryId: parseInt(form.categoryId),
        name: form.name,
        description: form.description || null,
        estimatedPrice: form.estimatedPrice ? parseFloat(form.estimatedPrice) : null,
        isActive: form.isActive
      };
      
      if (editingItem) {
        await equipmentService.updateSubcategory(editingItem.id, payload);
        toast.success('Item type updated');
      } else {
        await equipmentService.createSubcategory(payload);
        toast.success('Item type created');
      }
      closeDialog();
      loadSubcategories();
    } catch (e) {
      toast.error(e?.response?.data || e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const selectedCategory = categories.find(c => c.id === parseInt(selectedCategoryId));

  if (loading && categories.length === 0) return <SkeletonBlock lines={5} />;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h5">Equipment Item Types</Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            select size="small" label="Filter by Category" value={selectedCategoryId}
            onChange={(e) => setSelectedCategoryId(e.target.value)}
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="">All Categories</MenuItem>
            {categories.map(cat => (
              <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
            ))}
          </TextField>
          <IconButton onClick={loadSubcategories} size="small"><RefreshIcon /></IconButton>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => openDialog()}>
            Add Item Type
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Category</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Description</TableCell>
              <TableCell align="right">Est. Price (EUR)</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {subcategories.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">No item types found</TableCell>
              </TableRow>
            ) : subcategories.map((item) => (
              <TableRow key={item.id}>
                <TableCell>{item.categoryName}</TableCell>
                <TableCell><strong>{item.name}</strong></TableCell>
                <TableCell>{item.description || '-'}</TableCell>
                <TableCell align="right">{item.estimatedPrice?.toFixed(2) || '-'}</TableCell>
                <TableCell>
                  <Chip label={item.isActive ? 'Active' : 'Inactive'} color={item.isActive ? 'success' : 'default'} size="small" />
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => openDialog(item)}><EditIcon /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingItem ? 'Edit Item Type' : 'Add Item Type'}</DialogTitle>
        <DialogContent>
          <TextField
            select
            fullWidth
            label="Category"
            value={form.categoryId || ''}
            onChange={(e) => setForm({ ...form, categoryId: e.target.value })}
            required
            sx={{ mt: 1, mb: 2 }}
          >
            {categories.map(cat => (
              <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
            ))}
          </TextField>
          <TextField
            fullWidth label="Name" value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            required sx={{ mb: 2 }}
          />
          <TextField
            fullWidth multiline rows={2} label="Description"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth type="number" label="Estimated Price (EUR)"
            value={form.estimatedPrice}
            onChange={(e) => setForm({ ...form, estimatedPrice: e.target.value })}
            sx={{ mb: 2 }}
            inputProps={{ min: 0, step: 0.01 }}
          />
          <FormControlLabel
            control={<Switch checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} />}
            label="Active"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

